/**
 * Die Gliederung der Koordination — an EINER Stelle (N4, F69).
 *
 * Vorlage: Kopfkommentar von `seiten/heizlastkuehllast/abschnitte.ts` (N1).
 * Wer hier einen Eintrag ergänzt, folgt DEM Muster (`abschnitte.ts` je
 * Bereich, eingelesen in `navigation/struktur.ts`) — kein zweiter Weg.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * WAS GEMESSEN WURDE — UND WARUM ES NICHT ZWANZIG EINTRÄGE SIND
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * `seiten/koordination/` trägt 24 Dateien, davon 20 fachliche (die übrigen
 * vier — `Detailkarte`, `Filterleiste`, `Reiter`, `gemeinsam.ts` — sind
 * Hilfen). Der Auftrag (N4) übernahm dafür einen Vorschlag aus den
 * Dateinamen mit fünf Zwischenzeilen, sieben davon in einer neuen `regeln`.
 *
 * Gemessen an `Koordination.tsx` sind das aber NICHT zwanzig gleichrangige
 * interne Reiter — es sind VIER echte Reiter (`Reiter.tsx`: Arbeiten,
 * Planeraufgaben, Auswertung, Grundlagen, gesteuert über `?reiter=`), und
 * die 20 Dateien sind größtenteils reine ANZEIGE-Komponenten OHNE eigenen
 * Datenzugriff (Props rein, kein `api`-Aufruf) — Karten, in die die
 * 2.300-Zeilen-Seite zerlegt wurde (siehe deren eigener Kopfkommentar,
 * Abschnitt „Warum die Seite in eigene Komponenten aufgeteilt ist"), keine
 * Navigationsziele.
 *
 * Dreizehn davon bekommen hier TROTZDEM eine echte, ZUSÄTZLICHE Route
 * (`KoordinationSeiten.tsx`, jede mit eigenem, schlankem Datenzugriff über
 * dieselben Endpunkte, die `Koordination.tsx` ohnehin schon aufruft) — die
 * sieben Regelkarten plus Planungsabschnitte, Pflichtbauteile, Kollisionen
 * und Einstufung, Auswertung, Planeraufgaben, Aufnahme und Stand. Die
 * bestehende Seite `koordination` (mit ihren vier Reitern) bleibt
 * UNVERÄNDERT bestehen — das ist ein ZWEITER Weg zum selben Inhalt, kein
 * Ersatz, und kein bestehender Pfad ändert sich.
 *
 * SIEBEN bleiben bewusst ohne eigene Route (Plan Abschnitt 6: „wo keine
 * Seite existiert, ist der Punkt Arbeit und gehört in ein Paket, nicht in
 * die Leiste" — hier zusätzlich: wo eine Karte nur im Verbund mit anderen
 * Karten desselben Reiters funktioniert, wäre eine eigene Route eine
 * Aufteilung, die es in der Oberfläche nicht gibt):
 *
 *   - `Baum`, `Kollisionsliste`, `Vorschaubild` — ein zusammenhängendes
 *     Dreiergespann im Reiter „Arbeiten": die Baumauswahl steuert den
 *     Filter der Liste, ein Listenklick öffnet die Detailkarte, die das
 *     Vorschaubild einbettet. Eine eigene Route je Karte würde diesen
 *     Ablauf zerreißen, nicht nur benennen — der Umbau, den die Falle des
 *     Auftrags ausdrücklich ausschließt.
 *   - `Arbeitsvorrat` — eigener Korb-/Seiten-/Stufe-2-Zustand (~150 Zeilen
 *     in `Koordination.tsx`: Körbe laden, blättern, die Stufe-2-Rechnung
 *     `koordinationVorschlagen`, dieselbe Detailkarte wie oben). Eine
 *     eigenständige Kopie dieses Zustands wäre kein Navigations-, sondern
 *     ein Funktionsumbau.
 *   - `Bericht`, `Loesungen` — sind in der Oberfläche KEIN eigener Reiter,
 *     sondern zwei innerhalb von `Auswertung.tsx` IMMER gemeinsam gezeigte
 *     Teilkarten. Ein eigener Pfad je Karte würde eine Aufteilung
 *     vortäuschen, die nicht existiert; beide bleiben über die neue Route
 *     `koordination/auswertung` erreichbar.
 *   - `Grundlagen` — nach dem Herauslösen ihrer neun Kinder (sieben Regeln,
 *     Planungsabschnitte, Pflichtbauteile, Aufnahme und Stand) bleibt kein
 *     eigener Inhalt übrig; ein zehnter, eigener Eintrag wäre eine reine
 *     Dopplung auf dieselben Daten.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * N6 — DER UMBAU, DEN N4 AUSGESCHLOSSEN HATTE, UND WAS ER TATSÄCHLICH TRÄGT
 * ─────────────────────────────────────────────────────────────────────────
 *
 * N4 schloss den Umbau von `Koordination.tsx` ausdrücklich aus; N6 war der
 * Auftrag, ihn zu prüfen. Gemessen wurde, WELCHER Zustand tatsächlich
 * zwischen Baum, Kollisionsliste, Vorschaubild und Arbeitsvorrat geteilt
 * ist (Kopfkommentar N6 in `Koordination.tsx`):
 *
 *   - Baum ↔ Kollisionsliste teilen sich `baumGewaehlt`/`baumArt` — der
 *     Baum SCHREIBT, der Ladeeffekt der Liste LIEST (dieselbe Kopplung wie
 *     `filter`/`seite`, die schon vorher in `suchparams` standen). Das ist
 *     jetzt EBENFALLS in der URL (`?knoten=`, `?baumart=`) statt in
 *     `useState` — dieselbe Auswahl übersteht ein Neuladen der Seite, und
 *     eine Baumauswahl bleibt weiterhin filternd (Probe:
 *     `koordination-arbeiten.test.tsx`, Rotprobe gegen den Stand vor N6
 *     bestanden).
 *   - Kollisionsliste ↔ Arbeitsvorrat ↔ Vorschaubild (über `Detailkarte`)
 *     teilen sich `detail`/`detailLaedt` — DIESELBE Variable für zwei
 *     unabhängige Anzeigebereiche. Diese blieb BEWUSST in `useState`: Der
 *     Umschalt-Trick in `listeEntscheiden` (schließen/wieder-öffnen, um den
 *     neuen Status zu zeigen) müsste sonst durch einen Versionszähler
 *     ersetzt werden — ein Funktionsumbau der Statusanzeige, kein reiner
 *     Ortswechsel des Zustands, und genau das Risiko der Falle, vor der der
 *     Auftrag warnt.
 *
 * Trotz gehobenem Zustand bekommt KEINE der sieben Karten hier eine
 * zusätzliche Route: Baum/Kollisionsliste/Vorschaubild sind GLEICHZEITIG
 * sichtbare Bereiche EINES Bildschirms (nebeneinander, nicht nacheinander)
 * — eine eigene Route je Karte würde ein Nacheinander erzwingen, das es in
 * der Oberfläche nicht gibt. `Arbeitsvorrat` trägt weiterhin die von N4
 * gemessenen ~65 Zeilen eigenen Korb-/Stufe-2-Zustand (`Koordination.tsx`:
 * `korb`/`korbseite`/`stufe2`/`vf`/`vorrat` plus deren Ladefunktionen) —
 * eine eigenständige Kopie wäre der Funktionsumbau, den der Auftrag
 * ausdrücklich nicht verlangt. `Bericht`/`Loesungen`/`Grundlagen` bleiben
 * aus denselben, gegengeprüften Gründen wie bei N4 ohne Route.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 */

export interface KoordinationEintrag {
  /** Der Pfad UNTER /projekte/:id/ — genau wie in App.tsx. */
  pfad: string;
  name: string;
  /** Wozu die Seite da ist. Erscheint als Titel am Baumeintrag. */
  hinweis: string;
}

export interface KoordinationAbschnitt {
  /**
   * Die `art` selbst — dieselben Schlüssel wie `ART_BESCHRIFTUNG` in
   * `navigation/struktur.ts` (`planung`, `berechnung`, `daten`, `pruefung`),
   * plus die neue Zwischenzeile `regeln` (Plan Abschnitt 7/8).
   */
  name: string;
  eintraege: KoordinationEintrag[];
}

export const KOORDINATION_ABSCHNITTE: KoordinationAbschnitt[] = [
  {
    name: 'planung',
    eintraege: [
      {
        pfad: 'koordination/planungsabschnitte',
        name: 'Planungsabschnitte',
        hinweis: 'Die Abschnitte, in die das Bauwerk für die Koordination zerlegt ist',
      },
      {
        pfad: 'koordination/pflichtbauteile',
        name: 'Pflichtbauteile',
        hinweis: 'Bauteile, deren Durchdringung nie irrelevant eingestuft werden darf',
      },
    ],
  },
  {
    name: 'berechnung',
    eintraege: [
      {
        pfad: 'koordination/kollisionen-und-einstufung',
        name: 'Kollisionen und Einstufung',
        hinweis: 'Der zu prüfende Bestand und die Einstufung — „echt" muss die '
          + 'kleinste Gruppe bleiben',
      },
      {
        pfad: 'koordination/auswertung',
        name: 'Auswertung',
        hinweis: 'Lösungen nach Lösungsart und Gewerkpaar, Automatik-Freigaben und der Bericht',
      },
    ],
  },
  {
    name: 'daten',
    eintraege: [
      {
        pfad: 'koordination/aufnahme-und-stand',
        name: 'Aufnahme und Stand',
        hinweis: 'Die Koordinationsaufnahme je Gewerkemodell und die Kollisionssuche über den Rasterindex',
      },
    ],
  },
  {
    // Die sieben Regelseiten (Plan Abschnitt 7/8) — eine begründete
    // Ausnahme von den vier Standardzeilen, wie „Regeln" es für einen
    // Bereich mit vielen Unterseiten vorsieht.
    name: 'regeln',
    eintraege: [
      {
        pfad: 'koordination/regeln/einstufungsregeln',
        name: 'Einstufungsregeln',
        hinweis: 'Die Rangfolge, nach der eine Durchdringung als echt, vorgesehen oder irrelevant eingestuft wird',
      },
      {
        pfad: 'koordination/regeln/gewerkevorrang',
        name: 'Gewerkevorrang',
        hinweis: 'Wer weicht wem — die Rangfolge ZWISCHEN Gewerken',
      },
      {
        pfad: 'koordination/regeln/gleichrangregeln',
        name: 'Gleichrangregeln',
        hinweis: 'Wo der Gewerkevorrang nichts zu sagen hat, weil beide Seiten demselben Gewerk angehören',
      },
      {
        pfad: 'koordination/regeln/hoehenbaender',
        name: 'Höhenbänder',
        hinweis: 'Der freie Raum je Trasse, aus dem die Ausweichvorschläge der Stufe 2 gerechnet werden',
      },
      {
        pfad: 'koordination/regeln/loesungsarten',
        name: 'Lösungsarten',
        hinweis: 'Die Kategorien, in die eine Anweisung fällt',
      },
      {
        pfad: 'koordination/regeln/toleranzen',
        name: 'Toleranzen',
        hinweis: 'Montagezuschlag und Mindestabstände, gegen die gerechnet wird',
      },
      {
        pfad: 'koordination/regeln/ki-anbieter',
        name: 'KI-Anbieter',
        hinweis: 'Der Anbieter, der die KI-Bewertung einzelner Kollisionen liefert',
      },
    ],
  },
  {
    name: 'pruefung',
    eintraege: [
      {
        pfad: 'koordination/planeraufgaben',
        name: 'Planeraufgaben',
        hinweis: 'Ein Cluster ist eine Entscheidung — Kollisionen im vollen Deckenhohlraum, wo kein Gewerk mehr ausweichen kann',
      },
    ],
  },
];
